import { Card, Divider, Table, TablePaginationConfig } from "antd";
import { getCoursesByStudent } from "../../../services/course.service";
import { useContext, useEffect, useState } from "react";
import { IStudentReportData } from "../../../interfaces/reports-students.interface";
import { LIMIT } from "../../../constants/pagination.constant";
import Course from "../../../interfaces/course.interface";
import { myCoursesColumns } from "../utils";
import composedChart from "./chart";
import Title from "antd/es/typography/Title";
import rowData from "./rowData";
import { UserContext } from "../../../context/user.context";

interface StudentComponent {
  data: IStudentReportData;
  student?: string;
  params: { startDate: string; endDate: string; term: string; year: string };
}

interface TableParams {
  pagination?: TablePaginationConfig;
}

export interface DataType extends Course {
  studentsCourses?: any[];
  key: React.Key;
  coursesStaff?: any[];
}

const StudentCharts = ({ data, student, params }: StudentComponent) => {
  const { user } = useContext(UserContext);
  const barData: any = transformRegistrationData(
    data?.gradesByAssignment ?? [],
    false,
  );
  const [courses, setCourses] = useState<DataType[]>([]);
  const [tableParams, setTableParams] = useState<TableParams>({
    pagination: {
      current: 1,
      pageSize: LIMIT,
    },
  });

  const handleTableChange = (pagination: TablePaginationConfig) => {
    setTableParams({
      pagination,
    });
  };

  useEffect(() => {
    getCoursesByStudent(
      {
        userId: Number(student),
        institutionId: user?.institutionId!,
        limit: LIMIT,
        skip: 0,
      },
      params,
    ).then(({ list, count }) => {
      setCourses(
        list.map((c) => ({
          ...c,
          key: c.id,
        })),
      );
      setTableParams((current) => ({
        pagination: { ...current.pagination, total: count },
      }));
    });
  }, [tableParams.pagination?.current, data?.gradesByAssignment]);

  return (
    <>
      <div className="flex flex-1 flex-col">
        <Card
          style={{
            boxShadow:
              "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)",
          }}
        >
          <div className="flex flex-1 justify-between">
            <div className="flex-1">
              <Divider orientation="left" plain>
                Student's Courses
              </Divider>
              <Table
                columns={myCoursesColumns}
                dataSource={courses}
                bordered={true}
                onChange={handleTableChange}
                pagination={tableParams.pagination}
              ></Table>
            </div>
          </div>
        </Card>
        <Card className="mt-8">
          <div className="flex flex-1 justify-between">
            <div>
              <Divider orientation="left" plain>
                Total Courses
              </Divider>
              {composedChart({
                type: "donut",
                key: "courseCode",
                data: data?.totalCourses ?? [],
                config: {
                  dataKey: "count",
                  legendKey: "paid",
                  label: `Courses: ${data?.totalCourses?.length ?? []}`,
                  legend: ["Code", "Times", "Paid"],
                  colorSet: 1,
                },
              })}
              <Card className="m-0 p-0">
                <div className="flex gap-4 items-baseline pl-4">
                  <Title style={{ margin: 0 }} level={5}>
                    Average final grade:
                  </Title>
                  <Title style={{ margin: 0 }} level={4}>
                    {data?.averageFinalGrade ?? ""}
                  </Title>
                </div>
              </Card>
            </div>
            <div className="flex-1">
              <Divider orientation="left" plain>
                Registration Type Breakdown
              </Divider>
              <div className="ml-8">
                {composedChart({
                  type: "verticalBar",
                  key: "registrationType",
                  data: transformRegistrationData(
                    data?.totalByRegistrationType ?? [],
                    true,
                  ),
                  config: {
                    xField: "type",
                    yField: "total",
                  },
                })}
              </div>
            </div>
          </div>
        </Card>
        <Card className="mt-8">
          <div className="flex-1">
            <Divider orientation="left" plain>
              Students by status
            </Divider>
            {composedChart({
              type: "bar",
              key: "grade",
              data: barData.data ?? [],
              config: {
                xField: "date",
                yField: barData.headers,
                colorSet: 0,
              },
            })}
            {rowData(barData.data, "date", 0, true)}
          </div>
        </Card>
      </div>
    </>
  );
};

const transformRegistrationData = (data: any, type: boolean) => {
  if (type && data.length)
    return Object.entries(data[0]).map(([type, total]) => ({
      type,
      total,
    }));
  else {
    const rows = data.map(({ date, gradeName, grade }: any) => ({
      date,
      [gradeName]: grade,
    }));
    return {
      data: rows ?? [],
      headers: data.map((item: any) => item.gradeName),
    };
  }
};

export default StudentCharts;
